// Shared, forgiving search matching for recipe lists.
//
// Goals: "mac and cheese" finds "Mac & Cheese", stray punctuation or accents
// don't break matches, and word order doesn't matter — every word the user
// types just needs to appear somewhere in the recipe's text.

const normalize = (text) =>
  String(text || '')
    .toLowerCase()
    // Strip accents: "purée" -> "puree"
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    // Treat "&" and "+" as the word "and"
    .replace(/[&+]/g, ' and ')
    // Any other punctuation becomes a space ("mac-n-cheese" -> "mac n cheese")
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const searchableWords = (recipe) =>
  normalize(
    [
      recipe.title,
      recipe.description,
      ...(recipe.ingredients || []),
      ...(recipe.categories || []),
      recipe.creator,
      recipe.notes,
    ]
      .filter(Boolean)
      .join(' ')
  ).split(' ');

// A query term matches only at the START of a word: "cream" matches
// "creamy", but "ice" does NOT match "rice" or "juice".
const termMatches = (words, term) => words.some((w) => w.startsWith(term));

// Filler words that shouldn't be required to match ("mac and cheese"
// should still find "mac cheese bake").
const STOP_WORDS = new Set(['and', 'the', 'a', 'an', 'of', 'with', 'n']);

export const matchesRecipeSearch = (recipe, query) => {
  const q = normalize(query);
  if (!q) return true;

  const words = searchableWords(recipe);
  const queryWords = q.split(' ');
  const required = queryWords.filter((w) => !STOP_WORDS.has(w));
  // If the query is ONLY stop words ("and"), fall back to matching them.
  const terms = required.length > 0 ? required : queryWords;

  return terms.every((term) => termMatches(words, term));
};

// Count how many query words a recipe matches (for partial-match fallback).
const matchCount = (recipe, terms) => {
  const words = searchableWords(recipe);
  return terms.filter((term) => termMatches(words, term)).length;
};

// Filter a recipe list by a search query. Prefers recipes matching ALL words;
// if none do, falls back to the recipes matching the most words (so
// "cheddar mac" still surfaces "Mac & Cheese" when nothing has cheddar),
// best matches first.
export const filterRecipesBySearch = (recipes, query) => {
  const q = normalize(query);
  if (!q) return recipes;

  const exact = recipes.filter((r) => matchesRecipeSearch(r, query));
  if (exact.length > 0) return exact;

  const words = q.split(' ');
  const required = words.filter((w) => !STOP_WORDS.has(w));
  const terms = required.length > 0 ? required : words;
  if (terms.length < 2) return [];

  const scored = recipes
    .map((r) => ({ r, score: matchCount(r, terms) }))
    .filter(({ score }) => score > 0);
  scored.sort((a, b) => b.score - a.score);
  return scored.map(({ r }) => r);
};
